<!-- eslint-disable vue/no-mutating-props -->
<!-- This component intentionally mutates the wizard prop object as part of a shared reactive state pattern -->
<template>
  <div class="space-y-6">
    <p class="text-body text-gray-600 dark:text-gray-300">
      Tell us about your business. This information will be displayed on your website.
    </p>

    <!-- Logo upload -->
    <div>
      <label class="form-label">Business Logo</label>
      <div class="mt-2 flex items-center gap-6">
        <!-- Logo preview -->
        <div
          class="flex h-24 w-24 items-center justify-center overflow-hidden rounded-xl bg-gray-100 dark:bg-gray-800">
          <img
            v-if="wizard.website.logo_url"
            :src="wizard.website.logo_url"
            alt="Business logo"
            class="h-full w-full object-cover">
          <svg
            v-else
            class="h-12 w-12 text-gray-400 dark:text-gray-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </div>

        <!-- Upload button -->
        <div>
          <input
            ref="logoInput"
            type="file"
            accept="image/*"
            class="hidden"
            @change="handleLogoUpload">
          <button
            type="button"
            class="btn btn-secondary"
            :disabled="uploadingLogo"
            @click="$refs.logoInput.click()">
            <svg
              v-if="uploadingLogo"
              class="mr-2 h-4 w-4 animate-spin"
              fill="none"
              viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            {{ wizard.website.logo_url ? 'Change Logo' : 'Upload Logo' }}
          </button>
          <button
            v-if="wizard.website.logo_url"
            type="button"
            class="ml-2 text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
            @click="handleRemoveLogo">
            Remove
          </button>
          <p class="mt-1 text-caption text-gray-500 dark:text-gray-400">
            PNG, JPG up to 5MB
          </p>
        </div>
      </div>
    </div>

    <!-- Business name -->
    <div>
      <label for="businessName" class="form-label">
        Business Name <span class="text-red-500 dark:text-red-400">*</span>
      </label>
      <input
        id="businessName"
        v-model="wizard.website.business_name"
        type="text"
        class="form-input"
        placeholder="Your Business Name"
        @blur="saveField">
    </div>

    <!-- Industry -->
    <div>
      <label for="industry" class="form-label">
        Industry / Business Type
      </label>
      <select
        id="industry"
        v-model="wizard.website.industry"
        class="form-select"
        @change="saveField">
        <option value="">
          Select your industry...
        </option>
        <optgroup label="Home Services">
          <option value="hvac">
            HVAC / Heating & Cooling
          </option>
          <option value="plumbing">
            Plumbing
          </option>
          <option value="electrical">
            Electrical
          </option>
          <option value="roofing">
            Roofing
          </option>
          <option value="landscaping">
            Landscaping
          </option>
          <option value="painting">
            Painting
          </option>
          <option value="cleaning">
            Cleaning Services
          </option>
          <option value="pest_control">
            Pest Control
          </option>
          <option value="garage_doors">
            Garage Doors
          </option>
          <option value="pool_service">
            Pool Service
          </option>
          <option value="fencing">
            Fencing
          </option>
          <option value="general_contractor">
            General Contractor
          </option>
          <option value="handyman">
            Handyman Services
          </option>
          <option value="appliance_repair">
            Appliance Repair
          </option>
          <option value="locksmith">
            Locksmith
          </option>
          <option value="moving">
            Moving Services
          </option>
          <option value="restoration">
            Restoration Services
          </option>
        </optgroup>
        <optgroup label="Health & Wellness">
          <option value="medspa">
            Med Spa / Aesthetics
          </option>
          <option value="healthcare">
            Healthcare / Medical
          </option>
          <option value="dental">
            Dental
          </option>
          <option value="optometry">
            Optometry / Eye Care
          </option>
          <option value="chiropractic">
            Chiropractic
          </option>
          <option value="fitness">
            Fitness / Gym
          </option>
          <option value="spa">
            Day Spa & Wellness
          </option>
          <option value="salon">
            Hair Salon
          </option>
        </optgroup>
        <optgroup label="Professional Services">
          <option value="legal">
            Legal Services
          </option>
          <option value="finance">
            Finance / Accounting
          </option>
          <option value="consulting">
            Consulting
          </option>
          <option value="real_estate">
            Real Estate
          </option>
        </optgroup>
        <optgroup label="Food & Hospitality">
          <option value="restaurant">
            Restaurant
          </option>
          <option value="catering">
            Catering
          </option>
        </optgroup>
        <optgroup label="Automotive">
          <option value="automotive">
            Auto Repair / Services
          </option>
        </optgroup>
        <optgroup label="Creative & Technology">
          <option value="photography">
            Photography
          </option>
          <option value="technology">
            Technology / IT Services
          </option>
          <option value="wedding_services">
            Wedding Services
          </option>
        </optgroup>
        <optgroup label="Other">
          <option value="retail">
            Retail
          </option>
          <option value="pet_services">
            Pet Services
          </option>
          <option value="education">
            Education / Tutoring
          </option>
          <option value="nonprofit">
            Nonprofit
          </option>
          <option value="other">
            Other
          </option>
        </optgroup>
      </select>
      <p class="mt-1 text-caption text-gray-500 dark:text-gray-400">
        This helps us generate content and design tailored to your industry
      </p>

      <!-- Template gallery shortcut once industry is picked -->
      <div
        v-if="templateVerticalForIndustry"
        class="mt-3 flex items-center justify-between gap-3 p-3 rounded-lg bg-primary-50 dark:bg-primary-900/20 border border-primary-100 dark:border-primary-800">
        <div class="flex items-start gap-3">
          <svg class="w-5 h-5 text-primary-600 dark:text-primary-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <rect x="3" y="3" width="7" height="7" rx="1" stroke-width="1.5" />
            <rect x="14" y="3" width="7" height="7" rx="1" stroke-width="1.5" />
            <rect x="3" y="14" width="7" height="7" rx="1" stroke-width="1.5" />
            <rect x="14" y="14" width="7" height="7" rx="1" stroke-width="1.5" />
          </svg>
          <div>
            <p class="text-sm font-medium text-gray-900 dark:text-gray-100">
              We have hand-crafted templates for your industry
            </p>
            <p class="text-xs text-gray-600 dark:text-gray-300 mt-0.5">
              Skip the AI builder — start from a designer template instead.
            </p>
          </div>
        </div>
        <a
          :href="`/dashboard/website/${wizard.website.id}/templates`"
          class="flex-shrink-0 text-sm font-medium text-primary-700 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-300 whitespace-nowrap">
          Browse →
        </a>
      </div>
    </div>

    <!-- Custom Industry (shown when "Other" is selected) -->
    <div v-if="wizard.website.industry === 'other'" class="ml-0">
      <label for="industryDescription" class="form-label">
        Describe Your Industry <span class="text-red-500 dark:text-red-400">*</span>
      </label>
      <input
        id="industryDescription"
        v-model="wizard.website.industry_description"
        type="text"
        class="form-input"
        placeholder="e.g., Mobile Car Detailing, Event Planning, Home Staging..."
        @blur="saveField">
      <p class="mt-1 text-caption text-gray-500 dark:text-gray-400">
        Tell us what type of business you run so we can tailor your website content
      </p>
    </div>

    <!-- Tagline -->
    <div>
      <label for="tagline" class="form-label">Tagline</label>
      <input
        id="tagline"
        v-model="wizard.website.tagline"
        type="text"
        class="form-input"
        placeholder="A short description of your business"
        @blur="saveField">
      <p class="mt-1 text-caption text-gray-500 dark:text-gray-400">
        A brief tagline that appears below your business name
      </p>
    </div>

    <!-- Description -->
    <div>
      <label for="description" class="form-label">About Your Business</label>
      <textarea
        id="description"
        v-model="wizard.website.description"
        rows="4"
        class="form-textarea"
        placeholder="Tell potential customers about your business, experience, and what makes you stand out..."
        @blur="saveField" />
    </div>

    <!-- Contact information -->
    <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <div>
        <label for="phone" class="form-label">Phone Number</label>
        <input
          id="phone"
          v-model="wizard.website.phone"
          type="tel"
          class="form-input"
          placeholder="(555) 123-4567"
          @blur="saveField">
      </div>
      <div>
        <label for="email" class="form-label">Email Address</label>
        <input
          id="email"
          v-model="wizard.website.email"
          type="email"
          class="form-input"
          placeholder="contact@yourbusiness.com"
          @blur="saveField">
      </div>
    </div>

    <!-- Address -->
    <div>
      <label for="address" class="form-label">Business Address</label>
      <input
        id="address"
        v-model="wizard.website.address"
        type="text"
        class="form-input"
        placeholder="123 Main St, City, State ZIP"
        @blur="saveField">
      <p class="mt-1 text-caption text-gray-500 dark:text-gray-400">
        This will be displayed on your contact page
      </p>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'

const props = defineProps({
  wizard: { type: Object, required: true },
  mapKey: { type: String, required: true }
})

const uploadingLogo = ref(false)
const saveTimeout = ref(null)

// Industries we currently ship templates for. Keep in sync with
// SiteTemplate::VERTICALS (server-side). Showing the gallery CTA when an
// industry matches keeps the wizard from advertising templates we can't
// fulfill — the gallery itself filters on vertical.
const INDUSTRIES_WITH_TEMPLATES = new Set([
  'medspa', 'spa', 'salon', 'dental', 'legal', 'fitness'
])

const templateVerticalForIndustry = computed(() => {
  const industry = props.wizard?.website?.industry
  return industry && INDUSTRIES_WITH_TEMPLATES.has(industry) ? industry : null
})

async function saveField() {
  // Debounce saves
  if (saveTimeout.value) {
    clearTimeout(saveTimeout.value)
  }

  saveTimeout.value = setTimeout(async() => {
    try {
      await props.wizard.saveWebsite({
        business_name: props.wizard.website.business_name,
        tagline: props.wizard.website.tagline,
        description: props.wizard.website.description,
        phone: props.wizard.website.phone,
        email: props.wizard.website.email,
        address: props.wizard.website.address,
        industry: props.wizard.website.industry,
        industry_description: props.wizard.website.industry_description
      })
    } catch (err) {
      console.error('Failed to save:', err)
    }
  }, 500)
}

async function handleLogoUpload(event) {
  const file = event.target.files?.[0]
  if (!file) return

  // Validate file size (5MB max)
  if (file.size > 5 * 1024 * 1024) {
    alert('Logo must be less than 5MB')
    return
  }

  uploadingLogo.value = true

  try {
    await props.wizard.uploadLogo(file)
  } catch (err) {
    console.error('Failed to upload logo:', err)
    alert('Failed to upload logo. Please try again.')
  } finally {
    uploadingLogo.value = false
    event.target.value = '' // Reset input
  }
}

async function handleRemoveLogo() {
  try {
    await props.wizard.removeLogo()
  } catch (err) {
    console.error('Failed to remove logo:', err)
  }
}
</script>
